import React, { useState, useEffect } from "react";
import { authApi } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  FiUserPlus,
  FiUser,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiShield,
  FiMail,
  FiPhone,
  FiUsers,
  FiList,
  FiArrowLeft,
  FiSave,
  FiAlertCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Modal from "../components/Modal";

const ALL_PERMISSIONS = [
  { key: "dashboard", label: "Dashboard", desc: "Overview analytics & cards" },
  { key: "vendors", label: "Add Vendor", desc: "Vendors & suppliers catalog" },
  { key: "categories", label: "Categories", desc: "Categories & subcategories" },
  { key: "inventory", label: "Inventory", desc: "Items, specifications & catalog" },
  { key: "purchases", label: "Purchases", desc: "Record & view purchase orders" },
  { key: "clients", label: "Add Client", desc: "Customers & clients management" },
  { key: "sales", label: "Sales", desc: "Record & view client sales orders" },
  { key: "stock", label: "Stock", desc: "Real-time stock levels & alerts" },
  { key: "warranty", label: "Warranty Expire", desc: "Warranty expiration full tracker" },
  { key: "reports", label: "Reports", desc: "Stock movement & audit reports" },
];

const UsersPage = () => {
  const { user: currentUser, isAdmin, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("list"); // "list" | "form"
  const [editingUser, setEditingUser] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });

  const initialForm = {
    name: "",
    username: "",
    email: "",
    phone: "",
    type: "Staff",
    role: "user",
    status: "active",
    password: "",
    permissions: ["dashboard", "vendors", "inventory", "purchases", "sales", "stock", "warranty"],
  };
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const isDuplicateUsername = Boolean(
    formData.username &&
    formData.username.trim() &&
    (formData.username.trim().toLowerCase() === "superadmin" ||
      users.some(
        (u) =>
          u.username?.toLowerCase() === formData.username.trim().toLowerCase() &&
          (!editingUser || u.id !== editingUser.id)
      ))
  );

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await authApi.getUsers();
      setUsers(res.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleStartAdd = () => {
    setEditingUser(null);
    setFormData(initialForm);
    setActiveTab("form");
  };

  const handleStartEdit = (u) => {
    setEditingUser(u);
    const perms = Array.isArray(u.permissions)
      ? u.permissions
      : typeof u.permissions === "string"
        ? (() => {
          try {
            return JSON.parse(u.permissions || "[]");
          } catch {
            return [];
          }
        })()
        : [];

    setFormData({
      name: u.name || "",
      username: u.username || "",
      email: u.email || "",
      phone: u.phone || "",
      type: u.type || "Staff",
      role: u.role || "user",
      status: u.status || "active",
      password: "", // Leave blank if not changing
      permissions: u.role === "admin" ? ALL_PERMISSIONS.map((p) => p.key) : perms,
    });
    setActiveTab("form");
  };

  const handleBackToList = () => {
    setActiveTab("list");
    setEditingUser(null);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.warning("Full name is required");
      return;
    }
    if (!formData.username?.trim()) {
      toast.warning("Username is required");
      return;
    }

    const cleanUsername = formData.username.trim().toLowerCase().replace(/\s+/g, "");

    // Check duplicate username against current users list
    const isDuplicate = users.some(
      (u) =>
        u.username?.toLowerCase() === cleanUsername &&
        (!editingUser || u.id !== editingUser.id)
    );

    if (isDuplicate) {
      toast.error("This username already exists! Please choose a unique username.");
      return;
    }

    const payload = {
      ...formData,
      name: formData.name.trim(),
      username: cleanUsername,
    };

    try {
      setSubmitting(true);
      if (editingUser) {
        await authApi.updateUser(editingUser.id, payload);
        toast.success("User updated successfully");
      } else {
        await authApi.createUser(payload);
        toast.success("User created successfully");
      }
      setActiveTab("list");
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (userToToggle) => {
    if (userToToggle.id === currentUser?.id) {
      toast.error("You cannot deactivate your own logged-in account!");
      return;
    }
    const newStatus = userToToggle.status === "active" ? "inactive" : "active";
    try {
      await authApi.toggleUserStatus(userToToggle.id, newStatus);
      toast.success(`User set to ${newStatus}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || "Failed to change user status");
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.user) return;
    if (deleteModal.user.id === currentUser?.id) {
      toast.error("You cannot delete your own logged-in account!");
      setDeleteModal({ open: false, user: null });
      return;
    }
    try {
      await authApi.deleteUser(deleteModal.user.id);
      toast.success("User deleted successfully");
      setDeleteModal({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.message || "Failed to delete user");
    }
  };

  const filteredUsers = users.filter((u) => {
    // Hide Super Admin account from the user list completely
    if (
      u.role?.toLowerCase() === "superadmin" ||
      u.username?.toLowerCase() === "superadmin"
    ) {
      return false;
    }
    const q = search.toLowerCase();
    return (
      (u.name && u.name.toLowerCase().includes(q)) ||
      (u.username && u.username.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q)) ||
      (u.phone && u.phone.toLowerCase().includes(q)) ||
      (u.type && u.type.toLowerCase().includes(q)) ||
      (u.role && u.role.toLowerCase().includes(q))
    );
  });

  if (!isAdmin) {
    return (
      <div style={{ padding: "60px 20px", textAlign: "center" }}>
        <div
          style={{
            maxWidth: 440,
            margin: "0 auto",
            backgroundColor: "#fff",
            padding: "36px 28px",
            borderRadius: "var(--radius-lg)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <FiAlertCircle size={44} color="#DC2626" style={{ marginBottom: 14 }} />
          <h2 style={{ fontSize: "1.25rem", fontWeight: 800, color: "var(--navy)", marginBottom: 8 }}>
            Access Restricted
          </h2>
          <p style={{ color: "var(--slate-500)", fontSize: "0.88rem", lineHeight: 1.6 }}>
            The User Management panel is restricted to <strong>Administrators</strong>. You do not have permission to access or view this page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Top Header & Page Navigation Tabs */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)" }}>
            User Management <span style={{ fontSize: "0.82rem", color: "var(--primary)", backgroundColor: "var(--primary-light)", padding: "2px 8px", borderRadius: 6, fontWeight: 800 }}>Admin Only</span>
          </h2>
        </div>

        {/* View Mode Toggle: Directory vs Create on Page */}
        <div
          style={{
            display: "flex",
            backgroundColor: "#fff",
            padding: 4,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-200)",
            boxShadow: "var(--shadow-sm)",
            gap: 4,
          }}
        >
          <button
            onClick={() => {
              setActiveTab("list");
              setEditingUser(null);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              backgroundColor: activeTab === "list" ? "var(--primary)" : "transparent",
              color: activeTab === "list" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            <FiList size={16} />
            Users Directory
          </button>

          <button
            onClick={handleStartAdd}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "9px 18px",
              borderRadius: "var(--radius-sm)",
              border: "none",
              backgroundColor: activeTab === "form" ? "var(--primary)" : "transparent",
              color: activeTab === "form" ? "#fff" : "var(--slate-600)",
              fontWeight: 700,
              fontSize: "0.88rem",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
          >
            {editingUser ? <FiEdit2 size={16} /> : <FiUserPlus size={16} />}
            {editingUser ? `Edit: ${editingUser.name}` : "Create New User"}
          </button>
        </div>
      </div>

      {/* ===================== VIEW 1: CREATE / EDIT USER ON PAGE ===================== */}
      {activeTab === "form" && (
        <div className="fade-in">
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
              marginBottom: 30,
            }}
          >
            {/* Top Title Bar */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid var(--slate-100)",
                backgroundColor: "var(--slate-50)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "var(--radius-md)",
                    backgroundColor: "var(--primary-light)",
                    color: "var(--primary)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FiUserPlus size={22} />
                </div>
                <div>
                  <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
                    {editingUser ? `Edit User: ${editingUser.name}` : "Create New User"}
                  </h3>
                  <p style={{ fontSize: "0.82rem", color: "var(--slate-500)", margin: "3px 0 0 0" }}>
                    {editingUser
                      ? "Update operator details, password, system role, and access rights."
                      : "Register a new user account with credentials and tailored sidebar module permissions."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={handleBackToList}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 14px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  backgroundColor: "#fff",
                  color: "var(--slate-700)",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <FiArrowLeft size={16} /> Back to Users Directory
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handleSaveUser} style={{ padding: "26px 28px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                  gap: 26,
                  marginBottom: 24,
                }}
              >
                {/* Left Section: Account Credentials & Identity */}
                <div
                  style={{
                    backgroundColor: "var(--slate-50)",
                    border: "1px solid var(--slate-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 22,
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.88rem",
                      fontWeight: 700,
                      color: "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 16,
                      borderBottom: "1px solid var(--slate-200)",
                      paddingBottom: 8,
                    }}
                  >
                    <FiUsers size={17} /> 1. User Identity & Credentials
                  </div>

                  {/* Full Name */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. John Doe or Muhammad Ali"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>

                  {/* Username */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <label style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)" }}>
                        Username *
                      </label>
                      {formData.username && (
                        <span
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            color: isDuplicateUsername ? "#DC2626" : "#059669",
                          }}
                        >
                          {isDuplicateUsername ? (
                            <>
                              <FiAlertCircle size={13} /> Username already taken
                            </>
                          ) : (
                            <>
                              <FiCheckCircle size={13} /> Username available
                            </>
                          )}
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      required
                      placeholder="e.g. johndoe or user123"
                      value={formData.username}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          username: e.target.value.toLowerCase().replace(/\s+/g, ""),
                        })
                      }
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "var(--radius-md)",
                        border: `1px solid ${isDuplicateUsername ? "#EF4444" : "var(--slate-300)"}`,
                        fontSize: "0.9rem",
                        backgroundColor: isDuplicateUsername ? "#FEF2F2" : "#fff",
                        outline: "none",
                      }}
                    />
                    {isDuplicateUsername && (
                      <div style={{ fontSize: "0.75rem", color: "#DC2626", fontWeight: 600, marginTop: 4 }}>
                        A user with this username already exists. Please choose a unique username.
                      </div>
                    )}
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                      Phone / Mobile Number
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 0300 1234567"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--slate-300)",
                        fontSize: "0.9rem",
                        backgroundColor: "#fff",
                      }}
                    />
                  </div>

                  {/* User Type & Role in 2 columns */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                        User Type
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Staff, Manager, Admin"
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                        Role (Access Level)
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                          fontWeight: 600,
                        }}
                      >
                        <option value="user">User (Configurable)</option>
                        <option value="admin">Admin (Full Access)</option>
                      </select>
                    </div>
                  </div>

                  {/* Status and Password */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    <div>
                      <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                        Account Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                          fontWeight: 600,
                        }}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 700, color: "var(--slate-700)", marginBottom: 6 }}>
                        {editingUser ? "New Password (optional)" : "Password *"}
                      </label>
                      <input
                        type="password"
                        placeholder={editingUser ? "Leave blank to keep current" : "Min 6 characters"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid var(--slate-300)",
                          fontSize: "0.9rem",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Right Section: Module Access & Permissions Matrix */}
                <div
                  style={{
                    backgroundColor: "var(--slate-50)",
                    border: "1px solid var(--slate-200)",
                    borderRadius: "var(--radius-md)",
                    padding: 22,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 16,
                      borderBottom: "1px solid var(--slate-200)",
                      paddingBottom: 8,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.88rem",
                        fontWeight: 700,
                        color: "var(--primary)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <FiShield size={17} /> 2. Module Access & Sidebar Permissions
                    </div>

                    {formData.role !== "admin" && (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() =>
                            setFormData({ ...formData, permissions: ALL_PERMISSIONS.map((p) => p.key) })
                          }
                          style={{
                            padding: "4px 10px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderRadius: 4,
                            border: "1px solid var(--slate-300)",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            color: "var(--primary)",
                          }}
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, permissions: [] })}
                          style={{
                            padding: "4px 10px",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderRadius: 4,
                            border: "1px solid var(--slate-300)",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            color: "var(--slate-600)",
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>

                  <p style={{ margin: "0 0 14px", fontSize: "0.8rem", color: "var(--slate-500)" }}>
                    Select which modules and features this user will have access to in the sidebar navigation:
                  </p>

                  {formData.role === "admin" ? (
                    <div
                      style={{
                        padding: "16px 20px",
                        backgroundColor: "#EFF6FF",
                        border: "1px solid #BFDBFE",
                        borderRadius: "var(--radius-md)",
                        color: "#1D4ED8",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 12,
                      }}
                    >
                      <FiShield size={24} style={{ flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <div style={{ fontWeight: 800, fontSize: "0.92rem", marginBottom: 4 }}>
                          Administrator Full Access
                        </div>
                        <div style={{ fontSize: "0.82rem", lineHeight: 1.4 }}>
                          Users with the Admin role automatically have full unrestricted access to all modules, reports, system settings, and user management.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: 10,
                        maxHeight: 330,
                        overflowY: "auto",
                        paddingRight: 4,
                      }}
                    >
                      {ALL_PERMISSIONS.map((perm) => {
                        const isChecked = formData.permissions?.includes(perm.key);
                        return (
                          <label
                            key={perm.key}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 10,
                              padding: "10px 12px",
                              borderRadius: 6,
                              border: `1px solid ${isChecked ? "var(--primary)" : "var(--slate-200)"}`,
                              backgroundColor: isChecked ? "rgba(16, 185, 129, 0.06)" : "#fff",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const currentPerms = formData.permissions || [];
                                const updated = checked
                                  ? [...currentPerms, perm.key]
                                  : currentPerms.filter((k) => k !== perm.key);
                                setFormData({ ...formData, permissions: updated });
                              }}
                              style={{ marginTop: 2, cursor: "pointer", accentColor: "var(--primary)" }}
                            />
                            <div>
                              <div
                                style={{
                                  fontSize: "0.84rem",
                                  fontWeight: 700,
                                  color: isChecked ? "var(--navy)" : "var(--slate-700)",
                                }}
                              >
                                {perm.label}
                              </div>
                              <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", lineHeight: 1.25, marginTop: 2 }}>
                                {perm.desc}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Form Bottom Actions */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 12,
                  borderTop: "1px solid var(--slate-200)",
                  paddingTop: 18,
                }}
              >
                <button
                  type="button"
                  onClick={handleBackToList}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "var(--radius-md)",
                    border: "1px solid var(--slate-300)",
                    backgroundColor: "#fff",
                    color: "var(--slate-700)",
                    fontSize: "0.88rem",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || isDuplicateUsername}
                  title={isDuplicateUsername ? "Please provide a unique username before saving" : ""}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "10px 24px",
                    borderRadius: "var(--radius-md)",
                    border: "none",
                    backgroundColor: isDuplicateUsername ? "var(--slate-400)" : "var(--primary)",
                    color: "#fff",
                    fontSize: "0.88rem",
                    fontWeight: 700,
                    cursor: (submitting || isDuplicateUsername) ? "not-allowed" : "pointer",
                    boxShadow: "var(--shadow-md)",
                    transition: "background 0.2s ease",
                  }}
                >
                  <FiSave size={16} />
                  {submitting ? "Saving..." : editingUser ? "Update User Account" : "Create User Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================== VIEW 2: USERS DIRECTORY TABLE ===================== */}
      {activeTab === "list" && (
        <div className="fade-in">
          {/* Filter / Search Bar */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              padding: "16px 20px",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <div style={{ position: "relative", flex: 1, minWidth: 260, maxWidth: 400 }}>
              <FiSearch
                size={18}
                style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--slate-400)" }}
              />
              <input
                type="text"
                placeholder="Search by name, email, phone, role..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px 9px 38px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--slate-300)",
                  fontSize: "0.88rem",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: "0.85rem", color: "var(--slate-500)", fontWeight: 600 }}>
                Total Users: <strong>{users.length}</strong>
              </span>
              <button
                id="add-user-btn"
                onClick={handleStartAdd}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  backgroundColor: "var(--primary)",
                  color: "#fff",
                  padding: "9px 16px",
                  borderRadius: "var(--radius-md)",
                  border: "none",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                  boxShadow: "var(--shadow-md)",
                  transition: "all 0.15s",
                }}
              >
                <FiUserPlus size={16} /> Create New User
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--slate-200)",
              boxShadow: "var(--shadow-sm)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
                <thead>
                  <tr style={{ backgroundColor: "var(--slate-50)", borderBottom: "1px solid var(--slate-200)", textAlign: "left" }}>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>User / Name</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Contact Info</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Type</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Role</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Access & Permissions</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600 }}>Status</th>
                    <th style={{ padding: "12px 18px", color: "var(--slate-600)", fontWeight: 600, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: 36, textAlign: "center", color: "var(--slate-400)" }}>
                        No users found matching your search.
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => {
                      const isActive = u.status === "active";
                      return (
                        <tr
                          key={u.id}
                          style={{
                            borderBottom: "1px solid var(--slate-100)",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td style={{ padding: "14px 18px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <div
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "50%",
                                  backgroundColor: u.role === "admin" ? "var(--primary-light)" : "var(--slate-200)",
                                  color: u.role === "admin" ? "var(--primary)" : "var(--slate-700)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 700,
                                  fontSize: "0.9rem",
                                }}
                              >
                                {u.name ? u.name.charAt(0).toUpperCase() : "U"}
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, color: "var(--navy)" }}>{u.name}</div>
                                <div style={{ fontSize: "0.78rem", color: "var(--primary)", fontWeight: 600 }}>
                                  @{u.username}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td style={{ padding: "14px 18px" }}>
                            {u.email ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-700)" }}>
                                <FiMail size={13} color="var(--slate-400)" />
                                <span>{u.email}</span>
                              </div>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-500)", fontSize: "0.82rem" }}>
                                <FiUser size={13} color="var(--slate-400)" />
                                <span>@{u.username}</span>
                              </div>
                            )}
                            {u.phone && (
                              <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--slate-500)", fontSize: "0.8rem", marginTop: 3 }}>
                                <FiPhone size={13} color="var(--slate-400)" />
                                <span>{u.phone}</span>
                              </div>
                            )}
                          </td>

                          <td style={{ padding: "14px 18px", color: "var(--slate-700)", fontWeight: 600 }}>
                            {u.type || "Staff"}
                          </td>

                          <td style={{ padding: "14px 18px" }}>
                            <span
                              style={{
                                padding: "3px 10px",
                                borderRadius: 14,
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                backgroundColor: u.role === "admin" ? "var(--primary-light)" : "var(--slate-100)",
                                color: u.role === "admin" ? "var(--primary)" : "var(--slate-700)",
                                border: `1px solid ${u.role === "admin" ? "rgba(16, 185, 129, 0.3)" : "var(--slate-200)"}`,
                              }}
                            >
                              {u.role}
                            </span>
                          </td>

                          <td style={{ padding: "14px 18px", maxWidth: 280 }}>
                            {u.role === "admin" ? (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  padding: "3px 9px",
                                  borderRadius: 12,
                                  fontSize: "0.74rem",
                                  fontWeight: 700,
                                  backgroundColor: "var(--primary-light)",
                                  color: "var(--primary)",
                                }}
                              >
                                <FiShield size={12} /> Full Access (Admin)
                              </span>
                            ) : (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {(Array.isArray(u.permissions)
                                  ? u.permissions
                                  : typeof u.permissions === "string"
                                    ? (() => {
                                      try {
                                        return JSON.parse(u.permissions || "[]");
                                      } catch {
                                        return [];
                                      }
                                    })()
                                    : []
                                ).length === 0 ? (
                                  <span style={{ fontSize: "0.74rem", color: "var(--slate-400)", fontStyle: "italic" }}>
                                    No access granted
                                  </span>
                                ) : (
                                  (Array.isArray(u.permissions)
                                    ? u.permissions
                                    : typeof u.permissions === "string"
                                      ? (() => {
                                        try {
                                          return JSON.parse(u.permissions || "[]");
                                        } catch {
                                          return [];
                                        }
                                      })()
                                      : []
                                  ).map((permKey) => {
                                    const matched = ALL_PERMISSIONS.find((p) => p.key === permKey);
                                    return (
                                      <span
                                        key={permKey}
                                        style={{
                                          fontSize: "0.7rem",
                                          padding: "2px 6px",
                                          borderRadius: 4,
                                          backgroundColor: "var(--slate-100)",
                                          color: "var(--slate-700)",
                                          fontWeight: 600,
                                        }}
                                      >
                                        {matched ? matched.label : permKey}
                                      </span>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </td>

                          <td style={{ padding: "14px 18px" }}>
                            <button
                              onClick={() => handleToggleStatus(u)}
                              title={`Click to ${isActive ? "Deactivate" : "Activate"}`}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                padding: "4px 8px",
                                borderRadius: 6,
                                fontSize: "0.78rem",
                                fontWeight: 700,
                                backgroundColor: isActive ? "var(--success-bg)" : "var(--danger-bg)",
                                color: isActive ? "var(--success)" : "var(--danger)",
                              }}
                            >
                              {isActive ? <FiCheckCircle size={14} /> : <FiXCircle size={14} />}
                              {isActive ? "Active" : "Inactive"}
                            </button>
                          </td>

                          <td style={{ padding: "14px 18px", textAlign: "right" }}>
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                              <button
                                onClick={() => handleStartEdit(u)}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: "1px solid var(--slate-200)",
                                  backgroundColor: "#fff",
                                  color: "var(--slate-700)",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: "0.8rem",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "var(--primary-light)";
                                  e.currentTarget.style.color = "var(--primary)";
                                  e.currentTarget.style.borderColor = "var(--primary)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#fff";
                                  e.currentTarget.style.color = "var(--slate-700)";
                                  e.currentTarget.style.borderColor = "var(--slate-200)";
                                }}
                              >
                                <FiEdit2 size={14} /> Edit
                              </button>
                              <button
                                onClick={() => setDeleteModal({ open: true, user: u })}
                                disabled={u.id === currentUser?.id}
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: 6,
                                  border: "1px solid #FECACA",
                                  backgroundColor: "#FEF2F2",
                                  color: "var(--danger)",
                                  cursor: u.id === currentUser?.id ? "not-allowed" : "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: "0.8rem",
                                  opacity: u.id === currentUser?.id ? 0.4 : 1,
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "var(--danger)";
                                  e.currentTarget.style.color = "#fff";
                                  e.currentTarget.style.borderColor = "var(--danger)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#FEF2F2";
                                  e.currentTarget.style.color = "var(--danger)";
                                  e.currentTarget.style.borderColor = "#FECACA";
                                }}
                              >
                                <FiTrash2 size={14} /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, user: null })} maxWidth={400}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "var(--danger-bg)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <FiTrash2 size={24} />
          </div>
          <h4 style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>
            Confirm User Deletion
          </h4>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginBottom: 20 }}>
            Are you sure you want to permanently delete user{" "}
            <strong>"{deleteModal.user?.name || deleteModal.user?.username}"</strong>? This action cannot be undone.
          </p>
          <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
            <button
              type="button"
              onClick={() => setDeleteModal({ open: false, user: null })}
              style={{
                padding: "8px 18px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--slate-300)",
                backgroundColor: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteUser}
              style={{
                padding: "8px 20px",
                borderRadius: "var(--radius-md)",
                border: "none",
                backgroundColor: "var(--danger)",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Delete User
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsersPage;
